/**
 * Central consent store (§ 25 TDDDG / DSGVO).
 * Categories: statistics, marketing, external. "Necessary" is always on.
 * Stored in localStorage 'klanert-consent-v2'. Event: 'cookie-consent-changed'.
 */

export const CONSENT_KEY = 'klanert-consent-v2';
export const LEGACY_CONSENT_KEY = 'klanert-cookie-consent';
export const CONSENT_VERSION = '2026-10';
export const CONSENT_EVENT = 'cookie-consent-changed';
export const OPEN_SETTINGS_EVENT = 'open-cookie-settings';

export type ConsentCategory = 'statistics' | 'marketing' | 'external';

export interface ConsentState {
  version: string;
  timestamp: string;
  statistics: boolean;
  marketing: boolean;
  external: boolean;
}

/** Do-Not-Track or Global Privacy Control signal from the browser. */
export const hasPrivacySignal = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  const w = typeof window !== 'undefined' ? (window as unknown as { doNotTrack?: string }) : {};
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1' || nav.msDoNotTrack === '1' || w.doNotTrack === '1';
};

export const getConsent = (): ConsentState | null => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      version: CONSENT_VERSION,
      timestamp: String(parsed.timestamp || ''),
      statistics: parsed.statistics === true,
      marketing: parsed.marketing === true,
      external: parsed.external === true,
    };
  } catch {
    return null;
  }
};

/** Whether the user granted a category. Statistics/marketing are always false under DNT/GPC. */
export const hasConsent = (category: ConsentCategory): boolean => {
  const c = getConsent();
  if (!c || !c[category]) return false;
  if ((category === 'statistics' || category === 'marketing') && hasPrivacySignal()) return false;
  return true;
};

const TRACKING_COOKIE_PATTERNS = [/^_ga$/, /^_ga_/, /^_gid$/, /^_gcl_/, /^_pin_unauth$/, /^_pinterest_/, /^_derived_epik$/];
const STATISTICS_STORAGE_KEYS = [
  'klanert-session-id',
  'klanert-session-expiry',
  'klanert-session-start-fired',
  'klanert-session-intel',
  'klanert-returning-visitor',
];

const deleteCookie = (name: string) => {
  const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, '.klanert.com', 'klanert.com'];
  for (const d of domains) {
    document.cookie = `${name}=; ${expires}; path=/${d ? `; domain=${d}` : ''}`;
  }
};

export const clearTrackingData = () => {
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      if (name && TRACKING_COOKIE_PATTERNS.some((p) => p.test(name))) deleteCookie(name);
    });
  } catch { /* ignore */ }
  try {
    STATISTICS_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
    Object.keys(localStorage).forEach((k) => {
      if (/^_pin|^_gcl|^pinterest/i.test(k)) localStorage.removeItem(k);
    });
  } catch { /* ignore */ }
};

export const setConsent = (choice: { statistics: boolean; marketing: boolean; external: boolean }): ConsentState => {
  const previous = getConsent();
  const next: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    statistics: !!choice.statistics,
    marketing: !!choice.marketing,
    external: !!choice.external,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    localStorage.removeItem(LEGACY_CONSENT_KEY);
  } catch { /* ignore */ }

  const revoked =
    !!previous &&
    ((previous.statistics && !next.statistics) ||
      (previous.marketing && !next.marketing) ||
      (previous.external && !next.external));

  if (!next.statistics || !next.marketing) clearTrackingData();

  if (revoked) {
    window.location.reload();
    return next;
  }

  const w = window as unknown as { __loadStatistics?: () => void; __loadMarketing?: () => void };
  try {
    if (hasConsent('statistics')) w.__loadStatistics?.();
    if (hasConsent('marketing')) w.__loadMarketing?.();
  } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }));
  } catch { /* ignore */ }
  return next;
};

/** Grants a single category without touching the others (e.g. "Load Calendly"). */
export const grantCategory = (category: ConsentCategory) => {
  const c = getConsent();
  setConsent({
    statistics: c?.statistics ?? false,
    marketing: c?.marketing ?? false,
    external: c?.external ?? false,
    [category]: true,
  });
};

export const onConsentChange = (cb: (state: ConsentState | null) => void): (() => void) => {
  const handler = () => cb(getConsent());
  const storageHandler = (e: StorageEvent) => { if (e.key === CONSENT_KEY) handler(); };
  window.addEventListener(CONSENT_EVENT, handler);
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(CONSENT_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
};

export const openCookieSettings = () => {
  // Flag survives if the banner chunk has not mounted yet
  (window as unknown as { __openCookieSettingsPending?: boolean }).__openCookieSettingsPending = true;
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
};
